import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import { progressService, ProgressItem, JourneyProgress } from '../services/progressService';
import { InventoryService } from '../services/inventoryService';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { useLocale } from '../context/LocaleContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { useRelocalizeStepActions } from '../hooks/useRelocalizeStepActions';
import { withShareButton } from '../utils/stepShareUtils';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';
import { STEP_UI } from '../config/uiStrings';
import { getStepActionLabel } from '../config/stepActionLabels';
import { getStepActionDescription } from '../config/stepActionDescriptions';
import {
  getActionsIntro,
  getActionsToCompleteTitle,
  getCongratsBody,
  getCongratsHeading,
  getGuidanceModalTitle,
  getNextStepNavLabel,
  getPrevStepNavLabel,
  getRefreshPageLabel,
  getStepBreadcrumbs,
  getStepLoadingMessage,
  getStepPageQuote,
  getStepPageTitle,
} from '../config/stepPageCopy';

interface Step11Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step11: React.FC<Step11Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const { t } = useLocale();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  
  const [quietTimePlan, setQuietTimePlan] = useState<string>('');
  const [practiced3Days, setPracticed3Days] = useState<boolean>(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savedPlan, setSavedPlan] = useState(false);
  const [savePlanError, setSavePlanError] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: getStepActionLabel(11, 1),
      description: getStepActionDescription(11, 1),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GREEN_BOOK,
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: getStepActionLabel(11, 2),
      description: getStepActionDescription(11, 2),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GUIDANCE,
      buttonAction: () => setIsGuidanceOpen(true)
    },
    {
      id: 3,
      title: getStepActionLabel(11, 3),
      description: getStepActionDescription(11, 3),
      completed: false
    },
    {
      id: 4,
      title: getStepActionLabel(11, 4),
      description: getStepActionDescription(11, 4),
      completed: false
    },
    {
      id: 5,
      title: getStepActionLabel(11, 5),
      description: getStepActionDescription(11, 5),
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  useRelocalizeStepActions(originalActions, setActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const getShareContext = useCallback(
    () => ({ quietTimePlan, quietTimePracticed3Days: practiced3Days }),
    [quietTimePlan, practiced3Days]
  );
  const { openShareDialog, shareDialogProps } = useStepShareDialog(11, user, actions, getShareContext);

  const breadcrumbs: BreadcrumbItem[] = getStepBreadcrumbs(11);

  const loadQuietTimePlan = async () => {
    try {
      const inventoryData = await InventoryService.getInventory('step11-quiet-time-plan', '1.0');
      if (inventoryData && inventoryData.length > 0) {
        const planData = inventoryData[0];
        if (planData.plan) setQuietTimePlan(planData.plan);
        if (planData.practiced3Days) setPracticed3Days(planData.practiced3Days);
      }
    } catch (err) {
      console.error('Error loading quiet time plan:', err);
    }
  };

  const applyLoadedStepProgress = async (
    existingProgress: ProgressItem | null,
    journeyProgress: JourneyProgress | null
  ) => {
    if (journeyProgress) {
      setIsOptedOut(journeyProgress.opted_out);
    }

    if (existingProgress) {
      const savedData = existingProgress.data || {};
      setStepData(existingProgress);

      if (savedData.actions) {
        const merged = mergeStepActions(11, originalActions, savedData, !!existingProgress.completed);
        setActions(merged.actions);
        if (merged.migrationApplied) {
          await progressService.updateStepProgress({
            program: 'SAA',
            track: '12-steps',
            step_number: 11,
            step_title: 'Step Eleven',
            completed: !!existingProgress.completed,
            data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
          });
        }
      }
    }
  };

  const refreshStepData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step11', 'refreshStepData start');
      const [{ steps, journeyProgress }] = await Promise.all([
        perfTimeAsync('Step11 loadStepPagesProgress (parallel)', () =>
          progressService.loadStepPagesProgress('SAA', '12-steps', [11], { includeJourney: true })
        ),
        loadQuietTimePlan(),
      ]);

      await applyLoadedStepProgress(steps[11] ?? null, journeyProgress);
      clearError();
      perfSummary('Step Eleven page');
    } catch (err: any) {
      console.error('Error loading step progress:', err);
      handleError(err, t('step.page.error.load'));
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    refreshStepData();
  }, [user, stepWorkspaceRevision]);

  const handleHistoryStepUpdated = () => {
    setIsHistoryModalOpen(false);
    if (user) {
      void refreshStepData();
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  // Auto-save quiet time plan
  const saveQuietTimePlan = async () => {
    if (!user) return;
    
    setSavingPlan(true);
    setSavedPlan(false);
    setSavePlanError(false);
    
    try {
      await InventoryService.saveInventory('step11-quiet-time-plan', '1.0', [{
        plan: quietTimePlan,
        practiced3Days: practiced3Days
      }]);
      setSavedPlan(true);
      setTimeout(() => setSavedPlan(false), 3000);
    } catch (err) {
      console.error('Error saving quiet time plan:', err);
      setSavePlanError(true);
    } finally {
      setSavingPlan(false);
    }
  };

  const autoSaveQuietTimePlan = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      saveQuietTimePlan();
    }, 2000);
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), t('step.page.error.sign_in'));
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 11,
        step_title: 'Step Eleven',
        completed: allCompleted,
        data: stepDataWithActions(updatedActions, getStepActionSchema(11).actionSchemaVersion)
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Eleven - ${completedCount} of 5 actions completed`,
        progress_milestone: allCompleted ? 'step11_completed' : 'step11_in_progress'
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, t('step.page.error.save_progress'));
    }
  };

  const renderAction3Content = (action: ActionItem) => {
    if (action.id !== 3) return null;

    return (
      <div className={styles.actionContentIndent}>
        <div style={{ marginBottom: 16, width: '100%' }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ 
              fontWeight: 600, 
              color: '#333', 
              fontSize: '14px',
              display: 'block',
              marginBottom: '8px'
            }}>
              {t('step.11.plan.label')}
            </label>
            <textarea
              value={quietTimePlan}
              onChange={(e) => {
                setQuietTimePlan(e.target.value);
                autoSaveQuietTimePlan();
              }}
              onBlur={saveQuietTimePlan}
              placeholder={t('step.11.plan.placeholder')}
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={practiced3Days}
                onChange={(e) => {
                  setPracticed3Days(e.target.checked);
                  saveQuietTimePlan();
                }}
                style={{ width: '16px', height: '16px' }}
              />
              <span style={{ fontWeight: 500, color: '#333' }}>
                {t('step.11.plan.practiced_label')}
              </span>
            </label>
          </div>
          
          <div style={{ 
            textAlign: 'right', 
            minHeight: 24,
            fontSize: '14px',
            fontWeight: 500
          }}>
            {savingPlan && (
              <span style={{ color: '#6c757d' }}>
                {t('step.workspace.saving')}
              </span>
            )}
            {savedPlan && (
              <span style={{ color: '#28a745' }}>
                {t('step.workspace.saved_all')}
              </span>
            )}
            {savePlanError && (
              <span style={{ color: '#dc3545' }}>
                {t('step.workspace.save_failed')}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>{getStepPageTitle(11)}</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={11}
                stepTitle={getStepPageTitle(11)}
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>{getStepPageQuote(11)}
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>{getRefreshPageLabel()}</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>{getStepLoadingMessage()}</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>{getActionsToCompleteTitle(11)}</h2>
            <p className={styles.actionsDescription}>{getActionsIntro()}</p>
            
            <StepActions
              actions={withShareButton(11, actions, openShareDialog)}
              onActionChange={handleActionChange}
              stepNumber={11}
              renderActionContent={renderAction3Content}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>{getCongratsHeading()}</h3>
                  <p>{getCongratsBody(11)}</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-10" className={styles.prevStepLink}>
                      {getPrevStepNavLabel(11)}
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-12" className={styles.nextStepLink}>
                      {getNextStepNavLabel(11)}
                    </a>
                  </div>
                </div>
              </>
            )}

                    {!allCompleted && (
          <div className={styles.stepNavigation}>
            <div className={styles.navigationLinks}>
              <a href="/saa/12-steps/step-10" className={styles.prevStepLink}>
                {getPrevStepNavLabel(11)}
              </a>
              <span></span>
              <a href="/saa/12-steps/step-12" className={styles.nextStepLink}>
                {getNextStepNavLabel(11)}
              </a>
            </div>
          </div>
        )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-eleven start>>"
        endTag="<<step-eleven end>>"
      />

      {/* ContentReader for How to Work Step Eleven */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_11_Guidance.md"
        startTag="<<working-step-eleven start>>"
        endTag="<<working-step-eleven end>>"
        title={getGuidanceModalTitle(11)}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={11}
        stepTitle={getStepPageTitle(11)}
        stepData={stepData}
        onStepUpdated={handleHistoryStepUpdated}
      />

      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step11; 